import { FC, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

import { PropsWithClassName } from "@/types/PropsWithClassName";
import { cn } from "@/utils/classNames.ts";
import { passToRoute } from "@/utils/passToRoute.ts";
import Routes from "@/router/Routes.ts";
import { dateRange } from "$/utils/dayjs";
import dayjs from "$/utils/dayjs";
import UnsplashCredit from "@/components/ui/UnsplashCredit/UnsplashCredit.tsx";

type TripBlockRichProps = PropsWithClassName<{
  id: string;
  name: string;
  arrivalDate?: string | null;
  departureDate?: string | null;
  duration?: number | null;
  src?: string;
  tags?: string[];
  title?: string;
  href?: string;
  statusLabel?: string;
  statusTone?: "neutral" | "error";
  action?: ReactNode;
  photoCredit?: { name: string; profileUrl?: string | null; sourceUrl?: string | null };
}>;

const TripBlockRich: FC<TripBlockRichProps> = ({
  className,
  id,
  name,
  arrivalDate,
  departureDate,
  duration,
  src,
  tags,
  href,
  statusLabel,
  statusTone = "neutral",
  action,
  photoCredit,
}) => {
  const { t } = useTranslation();

  const derivedStatus = useMemo(() => {
    const today = dayjs().startOf("day");

    if (departureDate && dayjs(departureDate).endOf("day").isBefore(today)) {
      return t("tripNavbar.past", { defaultValue: "Past" });
    }

    if (arrivalDate) {
      const daysAway = dayjs(arrivalDate).startOf("day").diff(today, "day");

      if (daysAway > 0) {
        return t("tripNavbar.days_away", {
          count: daysAway,
          defaultValue: "{{count}} days away",
        });
      }
    }

    return null;
  }, [arrivalDate, departureDate, t]);
  const status = statusLabel ?? derivedStatus;

  const dateLabel =
    arrivalDate && departureDate
      ? dateRange(arrivalDate, departureDate, true)
      : duration
        ? duration > 1
          ? t("label.days_plural", { count: duration })
          : t("label.days_singular", { count: duration })
        : null;

  return (
    <article
      className={cn(
        "group overflow-hidden rounded-lg border border-transparent bg-white shadow-sm transition-transform duration-300 hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md",
        className,
      )}
    >
      <Link
        to={href ?? passToRoute(Routes.TRIP, { id })}
        className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        <div className="relative h-[170px] w-full overflow-hidden bg-primary/10">
          {src ? (
            <img
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              src={src}
              alt={name}
            />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-primary/25 via-primary/10 to-transparent" />
          )}

          {status ? (
            <span
              className={cn(
                "absolute left-3 top-3 px-2.5 py-1 font-sans text-xs shadow-sm backdrop-blur-sm",
                statusTone === "error"
                  ? "rounded-full bg-error/10 font-semibold uppercase tracking-wide text-error"
                  : "rounded font-medium text-base-content",
              )}
            >
              {status}
            </span>
          ) : null}
        </div>

        <div className="flex min-h-[80px] flex-col px-3 pb-3 pt-2">
          {tags?.length ? (
            <p className="truncate font-sans text-xs text-primary">
              {tags.slice(0, 2).join(" · ")}
            </p>
          ) : null}

          <div className="flex items-center justify-between gap-3">
            <h3 className="min-w-0 truncate font-feature text-xl font-semibold text-base-content">
              {name}
            </h3>

            <span className="inline-flex shrink-0 items-center gap-1 font-sans text-sm font-semibold text-primary">
              {t("button.open_trip")}
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </span>
          </div>

          {dateLabel ? (
            <p className="mt-1 font-sans text-xs text-text-secondary">
              {dateLabel}
            </p>
          ) : null}
        </div>
      </Link>

      {photoCredit ? (
        <UnsplashCredit
          photographerName={photoCredit.name}
          photographerUrl={photoCredit.profileUrl}
          sourceUrl={photoCredit.sourceUrl}
          className="px-3 pb-3 text-text-secondary"
        />
      ) : null}

      {action ? <div className="flex justify-end px-3 pb-3">{action}</div> : null}
    </article>
  );
};

export default TripBlockRich;